// src/pages/dashboard-pages/inventory/InventoryPage.tsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { inventoryApi } from '../../../services/inventoryVendorApi';
import backendApi from '../../../services/backendApi';
import breakdownEquipmentCategoryMapping from '../forms/sites/Axon/cell_data/dropdownOptions.json';
import ToastNotification from '../simulation/ToastNotification';
import './InventoryPage.css';
import '../../../styles/np-theme.css';
import {
  Package,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Warehouse,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  Activity,
  FileText,
  Calendar,
  Download,
  RefreshCw,
  X,
  MoreVertical,
  ChevronDown,
  Upload,
} from 'lucide-react';
import InventoryHistory from './InventoryHistory';

type InventoryStatus = 'in_stock' | 'low_stock' | 'out_of_stock' | 'reserved';

export interface InventoryItem {
  id: number;
  item_code?: string;
  item_name?: string;
  category: string;
  quantity: number;
  unit: string;
  min_stock?: number;
  max_stock?: number;
  current_value?: number;
  unit_price?: number;
  location?: string;
  status: InventoryStatus;
  last_updated?: string;
  supplier?: string;
  batch_number?: string;
  expiry_date?: string | null;
  notes?: string;
  // New fields from requirements
  sno?: number | null;
  station?: string | null; // Kept for legacy compatibility if needed, though removed from view
  equipment_name?: string | null;
  description?: string | null;
  product_serial_number?: string | null;
  electrical_rating?: string | null;
  current_rating?: string | null;
  voltage_rating?: string | null;
  power_rating?: string | null;
  reactive_power_rating?: string | null;
  apparent_power_rating?: string | null;
  frequency_rating?: string | null;
  capacity_amp_kw_kva?: string | null;
  make?: string | null;
  datasheet?: string | null;
  installed_quantity?: number | null;
  spare_availability?: number | null; // Changed to number
  consumption_pattern_last_three_months?: number | null;
  consumption_pattern_last_six_months?: number | null;
  consumption_pattern_last_twelve_months?: number | null;
  lead_time?: number | null;
  site_id?: number | null;
  siteId?: number | null; // Keep for now if used elsewhere
  site_code?: string | null;
  siteCode?: string | null;
  site?: {
    site_id?: number | null;
    site_code?: string | null;
    id?: number | null;
    code?: string | null;
    name?: string | null;
  } | null;
  user_id?: number | null;
  userId?: number | null;
  user?: {
    id?: number | null;
    username?: string | null;
    email?: string | null;
  } | null;
  created_by?: number | string | null;
  updated_by?: number | string | null;
}

interface InventoryItemInput {
  quantity: number;
  minStock: number;
  maxStock: number;
}

type EquipmentCategoryMapping = Record<string, string[]>;
const INVENTORY_AUTO_REFRESH_MS = 30000;

const InventoryPage = () => {
  const { site: siteParam } = useParams<{ site?: string }>();

  const routeSiteCode = useMemo(() => {
    const normalized = (siteParam || '').trim();
    if (!normalized || normalized.toLowerCase() === 'undefined') return null;
    return normalized.toUpperCase();
  }, [siteParam]);

  const initialSiteCode = useMemo(() => {
    const fromParam = routeSiteCode;
    const sessionSite = sessionStorage.getItem('tenant');
    const stored = localStorage.getItem('currentSite');
    return (fromParam || sessionSite || stored || 'ADMIN').toUpperCase();
  }, [routeSiteCode]);

  const [activeSiteCode, setActiveSiteCode] = useState(initialSiteCode);
  const [userScopeMeta, setUserScopeMeta] = useState<{
    userId: number | null;
    siteId: number | null;
    siteCode: string;
    canViewAll: boolean;
  }>({
    userId: null,
    siteId: null,
    siteCode: initialSiteCode,
    canViewAll: false,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ show: boolean; type: 'success' | 'error' | 'info'; title: string; message: string }>({
    show: false,
    type: 'success',
    title: '',
    message: ''
  });
  const [viewingItem, setViewingItem] = useState<InventoryItem | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [siteName, setSiteName] = useState<string>('');
  const site = activeSiteCode;

  useEffect(() => {
    setActiveSiteCode(initialSiteCode);
    setUserScopeMeta(prev => ({ ...prev, siteCode: initialSiteCode }));
    if (initialSiteCode && initialSiteCode !== 'ADMIN') {
      localStorage.setItem('currentSite', initialSiteCode);
      sessionStorage.setItem('tenant', initialSiteCode);
    }
  }, [initialSiteCode]);

  useEffect(() => {
    let isMounted = true;
    const resolveUserScope = async () => {
      try {
        const response = await backendApi.getCurrentUser();
        if (!isMounted || !response?.success || !response.user) return;

        const fetchedSiteCode = response.user.site?.site_code
          ? String(response.user.site.site_code).toUpperCase()
          : null;
        const fetchedSiteId =
          response.user.site?.site_id !== undefined && response.user.site?.site_id !== null
            ? Number(response.user.site.site_id)
            : null;
        const fetchedUserId =
          response.user.id !== undefined && response.user.id !== null
            ? Number(response.user.id)
            : null;

        const isSuperUser =
          Boolean((response.user as any)?.is_superuser) ||
          Boolean((response.user as any)?.isGlobalAdmin) ||
          String((response.user as any)?.role || '').toLowerCase() === 'admin';

        setUserScopeMeta(prev => ({
          userId: fetchedUserId ?? prev.userId,
          siteId: fetchedSiteId ?? prev.siteId,
          siteCode: fetchedSiteCode || prev.siteCode,
          canViewAll: isSuperUser || prev.canViewAll,
        }));

        if (fetchedSiteCode && !routeSiteCode) {
          setActiveSiteCode(prev => {
            if (prev === fetchedSiteCode) return prev;
            localStorage.setItem('currentSite', fetchedSiteCode);
            sessionStorage.setItem('tenant', fetchedSiteCode);
            return fetchedSiteCode;
          });
        }
      } catch (error) {
}
    };

    resolveUserScope();
    return () => {
      isMounted = false;
    };
  }, [routeSiteCode]);

  // Fetch site name from database
  useEffect(() => {
    const fetchSiteName = async () => {
      try {
        const response = await backendApi.getSites(false, activeSiteCode.toUpperCase());
        if (response.success && Array.isArray(response.sites)) {
          const matchedSite = response.sites.find(
            (item: any) => item.site_code && item.site_code.toUpperCase() === activeSiteCode.toUpperCase()
          ) || response.sites[0];

          if (matchedSite && matchedSite.site_name) {
            setSiteName(matchedSite.site_name);
          } else {
            setSiteName(activeSiteCode.toUpperCase());
          }
        }
      } catch (error) {
setSiteName(activeSiteCode.toUpperCase());
      }
    };

    if (activeSiteCode) {
      fetchSiteName();
    }
  }, [activeSiteCode]);

  // Column width state and resize functionality
  const defaultColumnWidths = [
    120, // Edit
    70,  // SNo
    190, // Spare (Breakdown Category)
    220, // Equipment Name
    240, // Brief Description
    220, // Serial No.
    150, // OEM
    180, // Datasheet
    180, // Apparent Power
    170, // Active Power
    180, // Reactive Power
    150, // Voltage
    150, // Current
    150, // Frequency
    130, // Lead Time
    170, // Estimated Cost
    190, // Total Installed Quantity
    130, // In-Stock
    180, // Consumption (1 Month)
    180, // Consumption (3 Months)
    180, // Consumption (6 Months)
    190, // Consumption (12 Months)
  ];
  const [columnWidths, setColumnWidths] = useState<number[]>(() => {
    const saved = localStorage.getItem('inventory-column-widths-v4');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length === defaultColumnWidths.length) {
          return parsed;
        }
      } catch (e) {
        console.error('Error parsing saved column widths:', e);
      }
    }
    return defaultColumnWidths;
  });

  const resizeRef = useRef<{ columnIndex: number; startX: number; startWidth: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Save column widths to localStorage
  useEffect(() => {
    localStorage.setItem('inventory-column-widths-v4', JSON.stringify(columnWidths));
  }, [columnWidths]);

  // Handle resize start
  const handleResizeStart = useCallback((columnIndex: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = columnWidths[columnIndex];
    resizeRef.current = { columnIndex, startX, startWidth };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  // Handle resize move
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeRef.current) return;

      const { columnIndex, startX, startWidth } = resizeRef.current;
      const diff = e.clientX - startX;
      const newWidth = Math.max(50, startWidth + diff); // Minimum width of 50px

      setColumnWidths(prev => {
        const newWidths = [...prev];
        newWidths[columnIndex] = newWidth;
        return newWidths;
      });
    };

    const handleMouseUp = () => {
      if (resizeRef.current) {
        resizeRef.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    // Always add listeners, but they check resizeRef.current internally
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Header style - only dynamic width properties, applied to all THs
  const getHeaderStyle = (columnIndex: number): React.CSSProperties => {
    const isActionsColumn = columnIndex === 0;
    return {
      width: `${columnWidths[columnIndex]}px`,
      minWidth: `${columnWidths[columnIndex]}px`,
      maxWidth: `${columnWidths[columnIndex]}px`,
      backgroundColor: 'var(--np-tint)',
      position: 'sticky',
      top: 0,
      left: isActionsColumn ? 0 : 'auto',
      zIndex: isActionsColumn ? 151 : 100, // Actions column stays on top
      borderRight: isActionsColumn ? '1px solid var(--np-line)' : undefined,
    };
  };

  // Fetch inventory data
  const fetchInventory = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);

      // Fetch all inventory data - we'll do all filtering client-side for maximum accuracy
      const response = await inventoryApi.getAll(activeSiteCode, {});
      if (response.ok && response.data) {
        // Handle paginated response or direct array
        let inventoryData: InventoryItem[] = [];
        if (Array.isArray(response.data)) {
          inventoryData = response.data;
        } else if (response.data && typeof response.data === 'object' && 'results' in response.data) {
          inventoryData = Array.isArray((response.data as any).results) ? (response.data as any).results : [];
        }
        setInventory(inventoryData);
      } else {
        console.error('Failed to fetch inventory:', response.error);
        // Set empty array on error to prevent .map() errors
        setInventory([]);
      }

    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [activeSiteCode]);

  // Initial load - fetch all inventory data once
  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  // Keep inventory data fresh without forcing a page reload.
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return;
      }
      void fetchInventory(false);
    }, INVENTORY_AUTO_REFRESH_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [fetchInventory]);

  // Note: All filtering is done client-side for maximum accuracy and performance
  // No need to refetch when filters change

  // Sample inventory data (fallback - remove after testing)
  const [inventoryFallback] = useState<InventoryItem[]>([
    {
      id: 1,
      item_code: 'SOL-PAN-001',
      item_name: 'Solar Panel 545W Mono PERC',
      category: 'Solar Panels',
      quantity: 2450,
      unit: 'pcs',
      min_stock: 500,
      max_stock: 3000,
      current_value: 2450000,
      unit_price: 1000,
      location: 'Warehouse A - Rack 12',
      status: 'in_stock',
      last_updated: '2024-01-15',
      supplier: 'Trina Solar India',
      batch_number: 'TR-2024-001',
      expiry_date: null,
      notes: 'High efficiency mono PERC panels'
    },
    {
      id: 2,
      item_code: 'INV-SMA-002',
      item_name: 'SMA Sunny Tripower 25000TL',
      category: 'Inverters',
      quantity: 12,
      unit: 'pcs',
      min_stock: 5,
      max_stock: 20,
      current_value: 4200000,
      unit_price: 350000,
      location: 'Warehouse B - Section 3',
      status: 'in_stock',
      last_updated: '2024-01-14',
      supplier: 'SMA Solar Technology',
      batch_number: 'SMA-2024-045',
      expiry_date: null,
      notes: '25kW three-phase inverter'
    },
    {
      id: 3,
      item_code: 'CAB-4MM-003',
      item_name: 'DC Cable 4mmÂ² (1kV)',
      category: 'Cables & Wires',
      quantity: 3500,
      unit: 'meters',
      min_stock: 1000,
      max_stock: 5000,
      current_value: 175000,
      unit_price: 50,
      location: 'Warehouse C - Spool Rack',
      status: 'low_stock',
      last_updated: '2024-01-13',
      supplier: 'Polycab Wires',
      batch_number: 'POL-2024-012',
      expiry_date: null,
      notes: 'Red positive DC cable'
    },
    {
      id: 4,
      item_code: 'MTG-ALU-004',
      item_name: 'Aluminum Mounting Rail 6m',
      category: 'Mounting Systems',
      quantity: 180,
      unit: 'pcs',
      min_stock: 50,
      max_stock: 300,
      current_value: 450000,
      unit_price: 2500,
      location: 'Warehouse A - Outside Storage',
      status: 'in_stock',
      last_updated: '2024-01-12',
      supplier: 'Schletter India',
      batch_number: 'SCH-2024-089',
      expiry_date: null,
      notes: '6-meter aluminum rail for ground mount'
    },
    {
      id: 5,
      item_code: 'COM-MOD-005',
      item_name: 'RS485 Communication Module',
      category: 'Monitoring Systems',
      quantity: 8,
      unit: 'pcs',
      min_stock: 10,
      max_stock: 50,
      current_value: 96000,
      unit_price: 12000,
      location: 'Warehouse B - Electronics Section',
      status: 'low_stock',
      last_updated: '2024-01-10',
      supplier: 'ABB India',
      batch_number: 'ABB-2024-023',
      expiry_date: null,
      notes: 'RS485 modbus communication module'
    },
    {
      id: 6,
      item_code: 'SAF-HEL-006',
      item_name: 'Safety Helmet with Chin Strap',
      category: 'Safety Equipment',
      quantity: 45,
      unit: 'pcs',
      min_stock: 30,
      max_stock: 100,
      current_value: 45000,
      unit_price: 1000,
      location: 'Tool Room - Safety Gear',
      status: 'in_stock',
      last_updated: '2024-01-08',
      supplier: '3M India',
      batch_number: '3M-2024-156',
      expiry_date: '2026-01-08',
      notes: 'ANSI Z89.1 Type I certified'
    },
    {
      id: 7,
      item_code: 'TOO-MUL-007',
      item_name: 'Digital Multimeter Fluke 87V',
      category: 'Tools',
      quantity: 5,
      unit: 'pcs',
      min_stock: 3,
      max_stock: 15,
      current_value: 375000,
      unit_price: 75000,
      location: 'Tool Room - Electrical',
      status: 'low_stock',
      last_updated: '2024-01-05',
      supplier: 'Fluke Corporation',
      batch_number: 'FLK-2024-067',
      expiry_date: null,
      notes: 'True RMS multimeter for field testing'
    },
    {
      id: 8,
      item_code: 'SPR-FUS-008',
      item_name: 'DC Fuse 1000V 15A',
      category: 'Spare Parts',
      quantity: 250,
      unit: 'pcs',
      min_stock: 100,
      max_stock: 500,
      current_value: 37500,
      unit_price: 150,
      location: 'Warehouse C - Spare Parts Bin',
      status: 'in_stock',
      last_updated: '2024-01-03',
      supplier: 'ABB India',
      batch_number: 'ABB-2024-234',
      expiry_date: null,
      notes: 'Fast-acting DC fuse for inverter protection'
    }
  ]);

  const parseNumericValue = useCallback((value: number | string | null | undefined): number => {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  }, []);

  const getEstimatedCost = (item: InventoryItem): number => {
    const currentValue = parseNumericValue(item.current_value ?? null);
    if (currentValue > 0) return currentValue;

    const unitPrice = parseNumericValue(item.unit_price ?? null);
    const quantity = parseNumericValue(item.quantity ?? null);
    return unitPrice > 0 && quantity > 0 ? unitPrice * quantity : 0;
  };

  const getConsumptionOneMonth = (item: InventoryItem): number => {
    const twelveMonths = parseNumericValue(item.consumption_pattern_last_twelve_months ?? null);
    if (twelveMonths > 0) return Math.round(twelveMonths / 12);

    const threeMonths = parseNumericValue(item.consumption_pattern_last_three_months ?? null);
    if (threeMonths > 0) return Math.round(threeMonths / 3);

    const sixMonths = parseNumericValue(item.consumption_pattern_last_six_months ?? null);
    if (sixMonths > 0) return Math.round(sixMonths / 6);

    return 0;
  };

  const [newItem, setNewItem] = useState({
    item_code: '',
    item_name: '',
    category: '',
    quantity: '',
    unit: 'pcs',
    min_stock: '',
    max_stock: '',
    unit_price: '',
    location: '',
    supplier: '',
    batch_number: '',
    expiry_date: '',
    notes: '',
    // New fields
    sno: '',
    equipment_name: '',
    description: '',
    product_serial_number: '',
    electrical_rating: '',
    current_rating: '',
    voltage_rating: '',
    power_rating: '',
    reactive_power_rating: '',
    apparent_power_rating: '',
    frequency_rating: '',
    capacity_amp_kw_kva: '',
    make: '',
    datasheet: '',
    installed_quantity: '',
    spare_availability: 0,
    consumption_pattern_last_three_months: '',
    consumption_pattern_last_six_months: '',
    consumption_pattern_last_twelve_months: '',
    lead_time: ''
  });

  // Match Breakdown sheet dropdown mapping: Equipment -> BD Category (Spare).
  const equipmentCategoryMapping = breakdownEquipmentCategoryMapping as EquipmentCategoryMapping;

  const equipmentOptions = useMemo(() => {
    return Object.keys(equipmentCategoryMapping).sort((a, b) => a.localeCompare(b));
  }, [equipmentCategoryMapping]);

  const allSpareOptions = useMemo(() => {
    const spareSet = new Set<string>();
    Object.values(equipmentCategoryMapping).forEach((values) => {
      values.forEach((value) => {
        if (value && value.trim()) spareSet.add(value.trim());
      });
    });
    return Array.from(spareSet).sort((a, b) => a.localeCompare(b));
  }, [equipmentCategoryMapping]);

  const spareOptionsForSelectedEquipment = useMemo(() => {
    const selectedEquipment = (newItem.equipment_name || '').trim();
    if (!selectedEquipment) return allSpareOptions;
    return equipmentCategoryMapping[selectedEquipment] || [];
  }, [allSpareOptions, equipmentCategoryMapping, newItem.equipment_name]);

  useEffect(() => {
    const selectedEquipment = (newItem.equipment_name || '').trim();
    if (!selectedEquipment) return;

    const allowedSpareValues = equipmentCategoryMapping[selectedEquipment] || [];
    if (allowedSpareValues.length === 0) return;

    if (!newItem.category || !allowedSpareValues.includes(newItem.category)) {
      setNewItem((prev) => ({
        ...prev,
        category: allowedSpareValues[0],
      }));
    }
  }, [equipmentCategoryMapping, newItem.category, newItem.equipment_name]);

  const units = ['pcs', 'meters', 'kg', 'liters', 'sqm', 'rolls', 'boxes', 'sets'];

  const getStatusColor = (status: InventoryStatus): string => {
    switch (status) {
      case 'in_stock': return 'text-green-600 bg-green-100';
      case 'low_stock': return 'text-yellow-600 bg-yellow-100';
      case 'out_of_stock': return 'text-red-600 bg-red-100';
      case 'reserved': return 'text-[#0066A3] bg-[#E3F0F9]';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-IN', { month: 'short' });
    const year = String(date.getFullYear()).slice(-2);

    return `${day}${month}${year}`;
  };

  const getStatusIcon = (status: InventoryStatus): React.ReactElement => {
    switch (status) {
      case 'in_stock': return <CheckCircle className="w-3 h-3" />;
      case 'low_stock': return <AlertTriangle className="w-3 h-3" />;
      case 'out_of_stock': return <AlertTriangle className="w-3 h-3" />;
      case 'reserved': return <Clock className="w-3 h-3" />;
      default: return <Package className="w-3 h-3" />;
    }
  };

  const calculateStatus = (item: InventoryItemInput): InventoryStatus => {
    if (item.quantity <= 0) return 'out_of_stock';
    if (item.quantity <= item.minStock) return 'low_stock';
    if (item.quantity >= item.maxStock) return 'reserved';
    return 'in_stock';
  };

  const getNumericValue = (value: number | string | null | undefined): number => {
    if (value === null || value === undefined) return 0;
    const parsed = typeof value === 'string' ? parseFloat(value) : Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const normalizeStatus = (status?: string | null): InventoryStatus | null => {
    if (!status) return null;
    const normalized = status.toLowerCase().replace(/\s+/g, '_');
    if (normalized === 'in_stock' || normalized === 'low_stock' || normalized === 'out_of_stock' || normalized === 'reserved') {
      return normalized as InventoryStatus;
    }
    return null;
  };

  const getDerivedStatus = (item: InventoryItem): InventoryStatus => {
    const serverStatus = normalizeStatus(item.status);
    if (serverStatus) {
      return serverStatus;
    }
    const quantity = getNumericValue(item.quantity);
    const minStock = getNumericValue(item.min_stock);
    const maxStock = getNumericValue(item.max_stock);
    return calculateStatus({ quantity, minStock, maxStock });
  };

  // Advanced client-side filtering function

  // Enhanced search function that searches across multiple fields
  const applySearchFilter = (items: InventoryItem[]): InventoryItem[] => {
    if (!searchTerm) return items;

    const searchLower = searchTerm.toLowerCase();
    return items.filter(item => {
      const searchTerms = [
        item.item_code,
        item.item_name,
        item.location,
        item.supplier,
        item.batch_number,
        item.product_serial_number,
        item.make,
        item.equipment_name,
        item.description,
        item.notes,
        item.category,
        item.electrical_rating,
        item.current_rating,
        item.voltage_rating,
        item.power_rating,
        item.reactive_power_rating,
        item.apparent_power_rating,
        item.frequency_rating,
        item.capacity_amp_kw_kva,
        item.station,
        // Numeric fields as strings
        item.installed_quantity?.toString(),
        item.spare_availability?.toString(),
        item.consumption_pattern_last_three_months?.toString(),
        item.consumption_pattern_last_six_months?.toString(),
        item.consumption_pattern_last_twelve_months?.toString()
      ];

      return searchTerms.some(term => term?.toLowerCase().includes(searchLower));
    });
  };

  // Get unique values for filter dropdowns
  const getUniqueValues = (field: keyof InventoryItem): string[] => {
    const values = new Set<string>();
    inventory.forEach(item => {
      const value = item[field];
      if (value && typeof value === 'string' && value.trim()) {
        values.add(value);
      }
    });
    return Array.from(values).sort();
  };

  const uniqueEquipmentNames = getUniqueValues('equipment_name');
  const uniqueCategoriesFromData = getUniqueValues('category');
  const uniqueMakes = getUniqueValues('make');

  // Intelligent Column-based filtering
  const applyColumnFilters = (items: InventoryItem[]): InventoryItem[] => {
    return items.filter(item => {
      for (const [column, filterValue] of Object.entries(columnFilters)) {
        if (!filterValue) continue;

        const itemValue = String((item as any)[column] ?? '').toLowerCase();
        if (!itemValue.includes(filterValue.toLowerCase())) {
          return false;
        }
      }
      return true;
    });
  };

  // Define filterable columns as requested
  const filterableColumns = [
    { key: 'category', label: 'Spare' },
    { key: 'equipment_name', label: 'Equipment Name' },
    { key: 'description', label: 'Brief Description' },
    { key: 'product_serial_number', label: 'Serial No.' },
    { key: 'make', label: 'OEM' },
    { key: 'datasheet', label: 'Datasheet' },
    { key: 'apparent_power_rating', label: 'Apparent Power' },
    { key: 'power_rating', label: 'Active Power' },
    { key: 'reactive_power_rating', label: 'Reactive Power' },
    { key: 'voltage_rating', label: 'Voltage' },
    { key: 'current_rating', label: 'Current' },
    { key: 'frequency_rating', label: 'Frequency' },
    { key: 'lead_time', label: 'Lead Time' },
    { key: 'installed_quantity', label: 'Installed Qty' },
    { key: 'spare_availability', label: 'In-Stock' },
    { key: 'consumption_pattern_last_three_months', label: 'Consumption (3M)' },
    { key: 'consumption_pattern_last_six_months', label: 'Consumption (6M)' },
    { key: 'consumption_pattern_last_twelve_months', label: 'Consumption (12M)' },
  ];

  const handleSetColumnFilter = (column: string, value: string) => {
    setColumnFilters(prev => {
      const next = { ...prev };
      if (value) {
        next[column] = value;
      } else {
        delete next[column];
      }
      return next;
    });
  };

  const clearAllFilters = () => {
    setColumnFilters({});
    setSearchTerm('');
  };

  // Apply all filters
  const filteredInventory = useMemo(() => {
    let result = Array.isArray(inventory) ? [...inventory] : [];
    result = applySearchFilter(result);
    result = applyColumnFilters(result);
    // Sort by SNO in ascending order
    result.sort((a, b) => {
      const snoA = a.sno ?? 0;
      const snoB = b.sno ?? 0;
      return snoA - snoB;
    });
    return result;
  }, [inventory, searchTerm, columnFilters]);

  const inventoryCardStats = useMemo(() => {
    const source = inventory.length > 0 ? inventory : inventoryFallback;
    const baseStats = source.reduce(
      (acc, item) => {
        acc.totalInstalledQuantity += parseNumericValue(item.installed_quantity ?? null);
        acc.spareAvailabilityCount += parseNumericValue(item.spare_availability ?? null);
        acc.consumptionLastThreeMonths += parseNumericValue(item.consumption_pattern_last_three_months ?? null);
        acc.consumptionLastSixMonths += parseNumericValue(item.consumption_pattern_last_six_months ?? null);
        acc.consumptionLastTwelveMonths += parseNumericValue(item.consumption_pattern_last_twelve_months ?? null);
        // Count unique spares (items that have a spare_availability field set > 0)
        if (item.spare_availability !== null && item.spare_availability !== undefined && parseNumericValue(item.spare_availability) > 0) {
          acc.uniqueSparesAvailable += 1;
        }
        // Count unique equipment entries that have installed_quantity > 0
        if (item.installed_quantity !== null && item.installed_quantity !== undefined && parseNumericValue(item.installed_quantity) > 0) {
          acc.uniqueInstalledCount += 1;
        }
        return acc;
      },
      {
        totalInstalledQuantity: 0,
        spareAvailabilityCount: 0,
        consumptionLastThreeMonths: 0,
        consumptionLastSixMonths: 0,
        consumptionLastTwelveMonths: 0,
        uniqueSparesAvailable: 0,
        uniqueInstalledCount: 0,
      }
    );
    // Derived consumption metrics
    const consumptionMonthly = baseStats.consumptionLastTwelveMonths > 0
      ? Math.round(baseStats.consumptionLastTwelveMonths / 12)
      : (baseStats.consumptionLastThreeMonths > 0 ? Math.round(baseStats.consumptionLastThreeMonths / 3) : 0);
    const consumptionQuarterly = baseStats.consumptionLastThreeMonths;
    const consumptionHalfYearly = baseStats.consumptionLastSixMonths;
    const consumptionYearly = baseStats.consumptionLastTwelveMonths;
    const totalUniqueSpares = source.filter(i => i.spare_availability !== null && i.spare_availability !== undefined).length;

    return {
      ...baseStats,
      consumptionMonthly,
      consumptionQuarterly,
      consumptionHalfYearly,
      consumptionYearly,
      totalUniqueSpares,
    };
  }, [inventory, inventoryFallback, parseNumericValue]);

  // Normalize optional URL inputs so backend URLField validation doesn't reject them
  const normalizeOptionalUrl = (
    value: string | null | undefined,
    fieldLabel: string
  ): { value: string | null; error?: string } => {
    const trimmed = value?.trim();
    if (!trimmed) return { value: null };

    const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    try {
      const parsed = new URL(candidate);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        throw new Error('Unsupported protocol');
      }

      const hostname = parsed.hostname?.toLowerCase();
      const ipv4Pattern = /^(25[0-5]|2[0-4]\d|1?\d{1,2})(\.(25[0-5]|2[0-4]\d|1?\d{1,2})){3}$/;
      const ipv6Pattern = /^[0-9a-f:]+$/i;
      const hasDomainTld = hostname ? hostname.includes('.') : false;
      const isValidHost =
        !!hostname &&
        (hostname === 'localhost' ||
          hasDomainTld ||
          ipv4Pattern.test(hostname) ||
          ipv6Pattern.test(hostname));

      if (!isValidHost) {
        throw new Error('Hostname must include a domain (e.g. example.com)');
      }

      return { value: parsed.href };
    } catch {
      return {
        value: null,
        error: `${fieldLabel} must be a valid URL (e.g. https://example.com/spec.pdf).`
      };
    }
  };

  const showToast = (type: 'success' | 'error' | 'info', title: string, message: string) => {
    setToast({ show: true, type, title, message });
  };

  const handleAddItem = async () => {
    // Validate that equipment_name is provided
    if (!newItem.equipment_name) {
      showToast('error', 'Validation Error', 'Please fill in Equipment Name');
      return;
    }

    const quantity = parseFloat(newItem.quantity) || 0;
    const minStock = parseFloat(newItem.min_stock) || 0;
    const maxStock = parseFloat(newItem.max_stock) || 0;
    const unitPrice = parseFloat(newItem.unit_price) || 0;

    if (minStock > maxStock && maxStock > 0) {
      showToast('error', 'Validation Error', 'Minimum stock cannot be greater than maximum stock');
      return;
    }

    const datasheetUrl = normalizeOptionalUrl(newItem.datasheet, 'Datasheet URL');
    if (datasheetUrl.error) {
      showToast('error', 'Validation Error', datasheetUrl.error);
      return;
    }

    const toNullIfEmpty = (value: any) => (value === '' || value === undefined || value === null ? null : value);
    const parseNumber = (value: any) => {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? null : parsed;
    };

    const itemData = {
      // Required database fields
      item_code: newItem.item_code || `EQ-${Date.now()}`,
      item_name: newItem.item_name || newItem.equipment_name || 'Equipment Item',
      category: newItem.category || 'Other',
      quantity,
      min_stock: minStock,
      max_stock: maxStock,
      unit_price: unitPrice,
      unit: newItem.unit || 'pcs',
      location: toNullIfEmpty(newItem.location),
      supplier: toNullIfEmpty(newItem.supplier),
      batch_number: toNullIfEmpty(newItem.batch_number),
      expiry_date: toNullIfEmpty(newItem.expiry_date),
      notes: toNullIfEmpty(newItem.notes),
      // Equipment fields
      sno: newItem.sno ? parseInt(newItem.sno) : null,
      equipment_name: toNullIfEmpty(newItem.equipment_name),
      description: toNullIfEmpty(newItem.description),
      product_serial_number: toNullIfEmpty(newItem.product_serial_number),
      electrical_rating: toNullIfEmpty(newItem.electrical_rating),
      current_rating: toNullIfEmpty(newItem.current_rating),
      voltage_rating: toNullIfEmpty(newItem.voltage_rating),
      power_rating: toNullIfEmpty(newItem.power_rating),
      reactive_power_rating: toNullIfEmpty(newItem.reactive_power_rating),
      apparent_power_rating: toNullIfEmpty(newItem.apparent_power_rating),
      frequency_rating: toNullIfEmpty(newItem.frequency_rating),
      capacity_amp_kw_kva: toNullIfEmpty(newItem.capacity_amp_kw_kva),
      make: toNullIfEmpty(newItem.make),
      datasheet: datasheetUrl.value,
      installed_quantity: parseNumber(newItem.installed_quantity),
      spare_availability: parseNumber(newItem.spare_availability) || 0,
      consumption_pattern_last_three_months: parseNumber(newItem.consumption_pattern_last_three_months),
      consumption_pattern_last_six_months: parseNumber(newItem.consumption_pattern_last_six_months),
      consumption_pattern_last_twelve_months: parseNumber(newItem.consumption_pattern_last_twelve_months),
      lead_time: newItem.lead_time ? parseInt(newItem.lead_time) : null
    };

    try {
const response = await inventoryApi.create(itemData, activeSiteCode);
      if (response.ok && response.data) {
        await fetchInventory();
        setNewItem({
          item_code: '',
          item_name: '',
          category: '',
          quantity: '',
          unit: 'pcs',
          min_stock: '',
          max_stock: '',
          unit_price: '',
          location: '',
          supplier: '',
          batch_number: '',
          expiry_date: '',
          notes: '',
          sno: '',
          equipment_name: '',
          description: '',
          product_serial_number: '',
          electrical_rating: '',
          current_rating: '',
          voltage_rating: '',
          power_rating: '',
          reactive_power_rating: '',
          apparent_power_rating: '',
          frequency_rating: '',
          capacity_amp_kw_kva: '',
          make: '',
          datasheet: '',
          installed_quantity: '',
          spare_availability: 0,
          consumption_pattern_last_three_months: '',
          consumption_pattern_last_six_months: '',
          consumption_pattern_last_twelve_months: '',
          lead_time: ''
        });
        setShowAddModal(false);
        showToast('success', 'Success', 'Inventory item added successfully!');
      } else {
        // Show detailed error message if available
        const errorMessage = response.data?.message || response.data?.error || response.error || 'Failed to add item';
        const errorDetails = response.data?.errors ? JSON.stringify(response.data.errors, null, 2) : '';
        console.error('Inventory creation error - Status:', response.status);
        console.error('Inventory creation error - Data:', response.data);
        console.error('Inventory creation error - Errors:', errorDetails);
        showToast('error', 'Error', errorDetails ? `${errorMessage}\n${errorDetails}` : errorMessage);
      }
    } catch (error: any) {
      console.error('Inventory creation exception:', error);
      showToast('error', 'Error', error.message || 'Failed to add item');
    }
  };

  const handleDeleteItem = async (id: number): Promise<void> => {
    if (window.confirm('Are you sure you want to delete this inventory item? This action cannot be undone.')) {
      try {
        const response = await inventoryApi.delete(id, activeSiteCode);
        if (response.ok) {
          await fetchInventory();
          showToast('success', 'Success', 'Inventory item deleted successfully!');
        } else {
          showToast('error', 'Error', response.error || 'Failed to delete item');
        }
      } catch (error: any) {
        showToast('error', 'Error', error.message || 'Failed to delete item');
      }
    }
  };

  const handleEditItem = (item: InventoryItem): void => {
    setEditingItem(item);
    setNewItem({
      item_code: item.item_code || '',
      item_name: item.item_name || '',
      category: item.category || '',
      quantity: (item.quantity ?? 0).toString(),
      unit: item.unit || 'pcs',
      min_stock: (item.min_stock ?? 0).toString(),
      max_stock: (item.max_stock ?? 0).toString(),
      unit_price: (item.unit_price ?? 0).toString(),
      location: item.location || '',
      supplier: item.supplier || '',
      batch_number: item.batch_number || '',
      expiry_date: item.expiry_date || '',
      notes: item.notes || '',
      // New fields
      sno: item.sno?.toString() || '',
      equipment_name: item.equipment_name || '',
      description: item.description || '',
      product_serial_number: item.product_serial_number || '',
      electrical_rating: item.electrical_rating || '',
      current_rating: item.current_rating || '',
      voltage_rating: item.voltage_rating || '',
      power_rating: item.power_rating || '',
      reactive_power_rating: item.reactive_power_rating || '',
      apparent_power_rating: item.apparent_power_rating || '',
      frequency_rating: item.frequency_rating || '',
      capacity_amp_kw_kva: item.capacity_amp_kw_kva || '',
      make: item.make || '',
      datasheet: item.datasheet || '',
      installed_quantity: item.installed_quantity?.toString() || '',
      spare_availability: item.spare_availability || 0,
      consumption_pattern_last_three_months: item.consumption_pattern_last_three_months?.toString() || '',
      consumption_pattern_last_six_months: item.consumption_pattern_last_six_months?.toString() || '',
      consumption_pattern_last_twelve_months: item.consumption_pattern_last_twelve_months?.toString() || '',
      lead_time: item.lead_time?.toString() || ''
    });
    setShowAddModal(true);
  };

  const handleUpdateItem = async (): Promise<void> => {
    if (!editingItem) {
      showToast('error', 'Error', 'No item selected for editing');
      return;
    }

    // Validate that equipment_name is provided
    if (!newItem.equipment_name) {
      showToast('error', 'Validation Error', 'Please fill in Equipment Name');
      return;
    }

    const quantity = parseFloat(newItem.quantity) || 0;
    const minStock = parseFloat(newItem.min_stock) || 0;
    const maxStock = parseFloat(newItem.max_stock) || 0;
    const unitPrice = parseFloat(newItem.unit_price) || 0;

    if (minStock > maxStock && maxStock > 0) {
      showToast('error', 'Validation Error', 'Minimum stock cannot be greater than maximum stock');
      return;
    }

    const datasheetUrl = normalizeOptionalUrl(newItem.datasheet, 'Datasheet URL');
    if (datasheetUrl.error) {
      showToast('error', 'Validation Error', datasheetUrl.error);
      return;
    }

    const toNullIfEmpty = (value: any) => (value === '' || value === undefined || value === null ? null : value);
    const parseNumber = (value: any) => {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? null : parsed;
    };

    const itemData = {
      // Required database fields
      item_code: newItem.item_code || `EQ-${editingItem.id}-${Date.now()}`,
      item_name: newItem.item_name || newItem.equipment_name || 'Equipment Item',
      category: newItem.category || 'Other',
      quantity,
      min_stock: minStock,
      max_stock: maxStock,
      unit_price: unitPrice,
      unit: newItem.unit || 'pcs',
      location: toNullIfEmpty(newItem.location),
      supplier: toNullIfEmpty(newItem.supplier),
      batch_number: toNullIfEmpty(newItem.batch_number),
      expiry_date: toNullIfEmpty(newItem.expiry_date),
      notes: toNullIfEmpty(newItem.notes),
      // Equipment fields
      sno: newItem.sno ? parseInt(newItem.sno) : null,
      equipment_name: toNullIfEmpty(newItem.equipment_name),
      description: toNullIfEmpty(newItem.description),
      product_serial_number: toNullIfEmpty(newItem.product_serial_number),
      electrical_rating: toNullIfEmpty(newItem.electrical_rating),
      current_rating: toNullIfEmpty(newItem.current_rating),
      voltage_rating: toNullIfEmpty(newItem.voltage_rating),
      power_rating: toNullIfEmpty(newItem.power_rating),
      reactive_power_rating: toNullIfEmpty(newItem.reactive_power_rating),
      apparent_power_rating: toNullIfEmpty(newItem.apparent_power_rating),
      frequency_rating: toNullIfEmpty(newItem.frequency_rating),
      capacity_amp_kw_kva: toNullIfEmpty(newItem.capacity_amp_kw_kva),
      make: toNullIfEmpty(newItem.make),
      datasheet: datasheetUrl.value,
      installed_quantity: parseNumber(newItem.installed_quantity),
      spare_availability: parseNumber(newItem.spare_availability) || 0,
      consumption_pattern_last_three_months: parseNumber(newItem.consumption_pattern_last_three_months),
      consumption_pattern_last_six_months: parseNumber(newItem.consumption_pattern_last_six_months),
      consumption_pattern_last_twelve_months: parseNumber(newItem.consumption_pattern_last_twelve_months),
      lead_time: newItem.lead_time ? parseInt(newItem.lead_time) : null
    };

    try {
const response = await inventoryApi.update(editingItem.id, itemData, activeSiteCode);
      if (response.ok && response.data) {
        await fetchInventory();
        setEditingItem(null);
        setShowAddModal(false);
        setNewItem({
          item_code: '',
          item_name: '',
          category: '',
          quantity: '',
          unit: 'pcs',
          min_stock: '',
          max_stock: '',
          unit_price: '',
          location: '',
          supplier: '',
          batch_number: '',
          expiry_date: '',
          notes: '',
          sno: '',
          equipment_name: '',
          description: '',
          product_serial_number: '',
          electrical_rating: '',
          current_rating: '',
          voltage_rating: '',
          power_rating: '',
          reactive_power_rating: '',
          apparent_power_rating: '',
          frequency_rating: '',
          capacity_amp_kw_kva: '',
          make: '',
          datasheet: '',
          installed_quantity: '',
          spare_availability: 0,
          consumption_pattern_last_three_months: '',
          consumption_pattern_last_six_months: '',
          consumption_pattern_last_twelve_months: '',
          lead_time: ''
        });
        showToast('success', 'Success', 'Inventory item updated successfully!');
      } else {
        showToast('error', 'Error', response.error || 'Failed to update item');
      }
    } catch (error: any) {
      showToast('error', 'Error', error.message || 'Failed to update item');
    }
  };

  const extractSiteMeta = (item: InventoryItem) => {
    const siteObj = (item as any).site || {};
    const rawSiteId =
      item.site_id ??
      item.siteId ??
      siteObj?.site_id ??
      siteObj?.id ??
      null;
    const rawSiteCode =
      item.site_code ??
      item.siteCode ??
      siteObj?.site_code ??
      siteObj?.code ??
      null;

    return {
      siteId: rawSiteId !== undefined && rawSiteId !== null ? Number(rawSiteId) : null,
      siteCode: rawSiteCode ? String(rawSiteCode).toUpperCase() : null,
    };
  };

  const extractUserMeta = (item: InventoryItem) => {
    const userObj = (item as any).user || {};
    const rawUserId =
      item.user_id ??
      item.userId ??
      userObj?.id ??
      (typeof item.created_by === 'number' ? item.created_by : null);
    const rawUsername =
      userObj?.username ||
      (typeof item.created_by === 'string' ? item.created_by : null) ||
      '';

    return {
      userId: rawUserId !== undefined && rawUserId !== null ? Number(rawUserId) : null,
      username: rawUsername ? String(rawUsername) : '',
    };
  };

  const verifySiteOwnership = (siteId: number | null, siteCode: string | null) => {
    if (userScopeMeta.canViewAll) return true;
    const expectedSiteId = userScopeMeta.siteId;
    const expectedSiteCode = (userScopeMeta.siteCode || activeSiteCode || '').toUpperCase();

    if (expectedSiteId !== null) {
      if (siteId === null) return false;
      return Number(siteId) === Number(expectedSiteId);
    }

    if (siteCode) {
      return siteCode.toUpperCase() === expectedSiteCode;
    }

    return true;
  };

  const verifyUserOwnership = (userId: number | null) => {
    if (userScopeMeta.canViewAll) return true;
    if (userScopeMeta.userId === null) return true;
    if (userId === null) return false;
    return Number(userId) === Number(userScopeMeta.userId);
  };

  const formatBoolean = (value?: boolean | null) => (value ? 'Yes' : 'No');

  const sanitizeCsvValue = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number' && !Number.isFinite(value)) return '';
    if (value instanceof Date) return value.toISOString();
    return String(value);
  };

  const escapeCsvValue = (value: any): string => {
    const sanitized = sanitizeCsvValue(value);
    return `"${sanitized.replace(/"/g, '""')}"`;
  };

  const handleBulkUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset file input
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv', 'application/vnd.ms-excel'];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
      showToast('error', 'Invalid File', 'Please upload a valid CSV or Excel file.');
      return;
    }

    try {
      showToast('info', 'Uploading', 'Uploading and processing file...');
      const response = await inventoryApi.bulkUpload(file, activeSiteCode);

      if (response.ok) {
        const count = response.data?.count || 0;
        const errors = response.data?.errors || [];
        if (count > 0) {
          showToast('success', 'Upload Successful', `Successfully added ${count} items.${errors.length > 0 ? ` with ${errors.length} skipped rows.` : ''}`);
          await fetchInventory();
        } else {
          const errorDetails = errors.length > 0 ? `: ${errors[0]}` : '';
          showToast('error', 'Upload Partial/Failed', `No items added. ${errors.length} skipped${errorDetails}`);
        }
      } else {
        const errorMsg = response.error || 'Failed to upload file';
        showToast('error', 'Upload Failed', errorMsg);
        console.error('Bulk upload error:', response);
      }
    } catch (error: any) {
      showToast('error', 'Upload Error', error.message || 'An unexpected error occurred during upload.');
      console.error('Bulk upload exception:', error);
    }
  };

  const handleExportCSV = () => {
    try {
      if (filteredInventory.length === 0) {
        showToast('info', 'No Data', 'There are no inventory rows to export for the current filters.');
        return;
      }

      // Use filteredInventory directly as it represents what's visible on screen
      const scopedRows = filteredInventory;

      const headers = [
        'SNO',
        'SPARE',
        'EQUIPMENT NAME',
        'BRIEF DESCRIPTION',
        'SERIAL NO.',
        'OEM',
        'DATASHEET',
        'APPARENT POWER RATING',
        'ACTIVE POWER',
        'REACTIVE POWER',
        'VOLTAGE',
        'CURRENT',
        'FREQUENCY',
        'LEAD TIME',
        'ESTIMATED COST',
        'TOTAL INSTALLED QUANTITY',
        'IN-STOCK',
        'CONSUMPTION (1 MONTH)',
        'CONSUMPTION (3 MONTHS)',
        'CONSUMPTION (6 MONTHS)',
        'CONSUMPTION (12 MONTHS)',
      ];

      const rows = scopedRows.map((item, index) => {
        return [
          item.sno ?? index + 1,
          item.category ?? '',
          item.equipment_name ?? '',
          item.description ?? '',
          item.product_serial_number ?? '',
          item.make ?? '',
          item.datasheet ?? '',
          item.apparent_power_rating ?? '',
          item.power_rating ?? '',
          item.reactive_power_rating ?? '',
          item.voltage_rating ?? '',
          item.current_rating ?? '',
          item.frequency_rating ?? '',
          item.lead_time ?? '',
          getEstimatedCost(item),
          item.installed_quantity ?? '',
          item.spare_availability ?? 0,
          getConsumptionOneMonth(item),
          item.consumption_pattern_last_three_months ?? '',
          item.consumption_pattern_last_six_months ?? '',
          item.consumption_pattern_last_twelve_months ?? '',
        ].map(escapeCsvValue);
      });

      const csvContent = [
        headers.map(escapeCsvValue).join(','),
        ...rows.map(row => row.join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `inventory_${activeSiteCode || 'ADMIN'}_${new Date().toISOString().split('T')[0]}.csv`
      );
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showToast('success', 'Export Successful', `Exported ${scopedRows.length} row(s) successfully.`);
    } catch (error: any) {
      showToast('error', 'Export Failed', error.message || 'Failed to export data');
    }
  };

  const handleViewItem = async (item: InventoryItem) => {
    // Fetch full item details from backend to ensure all fields are available
    try {
      const response = await inventoryApi.getById(item.id, activeSiteCode);
      if (response.ok && response.data) {
        setViewingItem(response.data);
      } else {
        // Fallback to item from list if API call fails
        setViewingItem(item);
      }
    } catch (error) {
      console.error('Error fetching item details:', error);
      // Fallback to item from list if API call fails
      setViewingItem(item);
    }
    setShowViewModal(true);
  };

  // Use stats from API (already set via fetchInventory)

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0B1120] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0084CF]/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-900 dark:text-slate-100 font-normal text-lg">Loading inventory...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen inventory-page np-page w-full">
      {/* Toast Notification */}
      <ToastNotification
        show={toast.show}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={() => setToast({ ...toast, show: false })}
      />
      <style
        dangerouslySetInnerHTML={{
          __html: `
            /* Structure only — typography and colour come from styles/np-theme.css */
            .inventory-table { border-collapse: separate !important; border-spacing: 0 !important; width: 100%; }
            .inventory-table-thead {
              display: table-header-group !important;
              position: sticky !important;
              top: 0 !important;
              z-index: 50 !important;
              visibility: visible !important;
              opacity: 1 !important;
            }
            .inventory-table-thead tr { display: table-row; }
            .inventory-page .inventory-table thead th,
            .inventory-page .inventory-table-thead th,
            .inventory-page .inventory-table-header {
              display: table-cell !important;
              padding: 11px 16px !important;
              text-align: center !important;
              line-height: normal !important;
              white-space: nowrap !important;
              position: sticky !important;
              top: 0 !important;
              min-width: 100px !important;
            }
            .inventory-table thead th:last-child { border-right: none !important; }

            /* Ensure modals appear above sticky table headers */
            .inventory-page [class*="fixed"][class*="inset-0"] { z-index: 1000 !important; }

            .inventory-page table tbody td,
            .inventory-page .inventory-table tbody td {
              font-size: var(--np-fs-sm) !important;
              text-align: center !important;
              vertical-align: middle !important;
            }
            .inventory-page .inventory-sno-text-small { font-size: var(--np-fs-2xs) !important; color: var(--np-subtle) !important; }

            /* Sticky Edit column */
            .inventory-page .sticky-left-col {
              position: sticky !important;
              left: 0 !important;
              z-index: 110 !important;
              background-color: var(--np-surface) !important;
              color: var(--np-ink) !important;
              border-right: 1px solid var(--np-line) !important;
              box-shadow: none !important;
            }
            .inventory-page thead .sticky-left-col {
              background-color: var(--np-tint) !important;
              top: 0 !important;
              z-index: 150 !important;
            }
            .inventory-page tbody tr:hover .sticky-left-col { background-color: var(--np-tint) !important; }

            .inventory-page .history-popup-text,
            .inventory-page .history-popup-text *,
            .inventory-page .history-popup-title { color: var(--np-ink) !important; }
          `,
        }}
      />

      {/* Main Content */}
      <div className="np-theme w-auto ml-4 mr-10 px-3 sm:px-4 py-2 pt-20 sm:pt-24 mt-16">
        {/* Header Section */}
        <div className="mb-2">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
            <div className="np-pagehead">
              <div className="np-pagehead__mark">
                <Package className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h1 className="np-pagehead__title">Inventory Management</h1>
                <div className="np-pagehead__meta">
                  <span className="np-pagehead__site">{siteName || activeSiteCode || 'ADMIN'}</span>
                  <span className="np-pagehead__desc">Spare Consumption & Availability</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search all fields..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-48 sm:w-56 pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm bg-white shadow-sm"
                />
              </div>
              {/* Column Filters Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-all text-sm shadow-sm ${showFilterPanel || Object.keys(columnFilters).length > 0
                  ? 'bg-[#F2F8FC] border-[#C2DFF1] text-[#1C4D88]'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                title="Column Filters"
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">Filters {Object.keys(columnFilters).length > 0 ? `(${Object.keys(columnFilters).length})` : ''}</span>
              </motion.button>
              {(searchTerm || Object.keys(columnFilters).length > 0) && (
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-red-600 hover:text-red-700 font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
                >
                  Clear
                </button>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                accept=".csv,.xlsx,.xls"
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleExportCSV}
                disabled={filteredInventory.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                title="Export to CSV" aria-label="Export to CSV"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Download</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setEditingItem(null);
                  setNewItem({
                    item_code: '',
                    item_name: '',
                    category: '',
                    quantity: '',
                    unit: 'pcs',
                    min_stock: '',
                    max_stock: '',
                    unit_price: '',
                    location: '',
                    supplier: '',
                    batch_number: '',
                    expiry_date: '',
                    notes: '',
                    sno: '',
                    equipment_name: '',
                    description: '',
                    product_serial_number: '',
                    electrical_rating: '',
                    current_rating: '',
                    voltage_rating: '',
                    power_rating: '',
                    reactive_power_rating: '',
                    apparent_power_rating: '',
                    frequency_rating: '',
                    capacity_amp_kw_kva: '',
                    make: '',
                    datasheet: '',
                    installed_quantity: '',
                    spare_availability: 0,
                    consumption_pattern_last_three_months: '',
                    consumption_pattern_last_six_months: '',
                    consumption_pattern_last_twelve_months: '',
                    lead_time: ''
                  });
                  setShowAddModal(true);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all shadow-sm text-sm"
                title="Add new inventory item" aria-label="Add new inventory item"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Add Item</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleBulkUploadClick}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all shadow-sm text-sm"
                title="Upload bulk data" aria-label="Upload bulk data"
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">Bulk Import</span>
              </motion.button>
            </div>
          </div>
        </div>

        {/* Stats Cards - Compact */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-2">
          {/* Card 1: Installed Quantity + Unique Spares Quantity */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="np-kpi"
          >
            <div className="np-kpi__head">
              <p className="np-kpi__label">Installed Quantity</p>
              <span className="np-kpi__mark">
                <Warehouse className="w-3.5 h-3.5" />
              </span>
            </div>
            <div className="np-kpi__row">
              <div className="min-w-0">
                <p className="np-kpi__value">
                  {(inventoryCardStats.totalInstalledQuantity ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </p>
                <p className="np-kpi__hint">Total units installed</p>
              </div>
              <div className="np-kpi__side">
                <p className="np-kpi__subvalue">
                  {inventoryCardStats.uniqueInstalledCount ?? 0}
                </p>
                <p className="np-kpi__hint">Unique items</p>
              </div>
            </div>
          </motion.div>

          {/* Card 2: Total Spares Available + Unique Spares Available */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="np-kpi"
          >
            <div className="np-kpi__head">
              <p className="np-kpi__label">Spares Available</p>
              <span className="np-kpi__mark">
                <CheckCircle className="w-3.5 h-3.5" />
              </span>
            </div>
            <div className="np-kpi__row">
              <div className="min-w-0">
                <p className="np-kpi__value">
                  {(inventoryCardStats.spareAvailabilityCount ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </p>
                <p className="np-kpi__hint">Total spares</p>
              </div>
              <div className="np-kpi__side">
                <p className="np-kpi__subvalue">
                  {inventoryCardStats.uniqueSparesAvailable ?? 0}
                </p>
                <p className="np-kpi__hint">Unique available</p>
              </div>
            </div>
          </motion.div>

          {/* Card 3: Consumption Pattern - Mini Bar Chart */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="np-kpi"
          >
            <div className="np-kpi__head mb-2">
              <p className="np-kpi__label">Consumption Pattern</p>
              <span className="np-kpi__mark">
                <TrendingUp className="w-3.5 h-3.5" />
              </span>
            </div>
            {/* Mini bar chart */}
            <div className="flex items-end gap-1.5 h-10">
              {(() => {
                const monthly = inventoryCardStats.consumptionMonthly ?? 0;
                const quarterly = inventoryCardStats.consumptionQuarterly ?? 0;
                const halfYearly = inventoryCardStats.consumptionHalfYearly ?? 0;
                const yearly = inventoryCardStats.consumptionYearly ?? 0;
                const maxVal = Math.max(monthly, quarterly, halfYearly, yearly, 1);
                const bars = [
                  { label: 'M', value: monthly, color: 'bg-[#3EA3DA]' },
                  { label: 'Q', value: quarterly, color: 'bg-[#0084CF]' },
                  { label: 'H', value: halfYearly, color: 'bg-[#0084CF]' },
                  { label: 'Y', value: yearly, color: 'bg-[#0084CF]' },
                ];
                return bars.map((bar, i) => (
                  <div key={i} className="flex flex-col items-center flex-1 gap-0.5">
                    <div className="w-full relative flex items-end justify-center" style={{ height: '28px' }}>
                      <div
                        className={`w-full rounded-t ${bar.color} transition-all`}
                        style={{ height: `${Math.max((bar.value / maxVal) * 100, 8)}%`, minHeight: '3px' }}
                        title={`${bar.label}: ${bar.value.toLocaleString('en-IN')}`}
                      />
                    </div>
                    <span className="text-[8px] text-gray-500 inventory-stat-card-semibold">{bar.label}</span>
                  </div>
                ));
              })()}
            </div>
            <div className="flex justify-between mt-1.5 text-[8px] text-gray-400">
              <span className="inventory-stat-card-semibold">{(inventoryCardStats.consumptionMonthly ?? 0).toLocaleString('en-IN')}</span>
              <span className="inventory-stat-card-semibold">{(inventoryCardStats.consumptionQuarterly ?? 0).toLocaleString('en-IN')}</span>
              <span className="inventory-stat-card-semibold">{(inventoryCardStats.consumptionHalfYearly ?? 0).toLocaleString('en-IN')}</span>
              <span className="inventory-stat-card-semibold">{(inventoryCardStats.consumptionYearly ?? 0).toLocaleString('en-IN')}</span>
            </div>
          </motion.div>
        </div>

        {/* Column Filter Panel (expandable) */}
        <AnimatePresence>
          {showFilterPanel && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="bg-white rounded-xl shadow-md border border-gray-200 mb-4 overflow-hidden"
            >
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {filterableColumns.map((col) => (
                  <div key={col.key} className="flex flex-col gap-1">
                    <label className="text-[12px] inventory-filter-label-bold text-gray-900 uppercase tracking-wider ml-1">
                      {col.label}
                    </label>
                    <input
                      type="text"
                      placeholder={`Filter by ${col.label}...`}
                      value={columnFilters[col.key] || ''}
                      onChange={(e) => handleSetColumnFilter(col.key, e.target.value)}
                      className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all outline-none"
                    />
                  </div>
                ))}
              </div>
              {Object.keys(columnFilters).length > 0 && (
                <div className="px-4 pb-4 flex flex-wrap gap-2">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest self-center mr-1">Active:</span>
                  {Object.entries(columnFilters).map(([key, value]) => {
                    const col = filterableColumns.find(c => c.key === key);
                    if (!value) return null;
                    return (
                      <div key={key} className="flex items-center gap-1 bg-[#E3F0F9] text-[#1C4D88] px-2 py-0.5 rounded-full text-xs font-medium border border-[#C2DFF1]">
                        <span>{col?.label}:</span>
                        <span className="text-[#0D2747]">{value}</span>
                        <button onClick={() => handleSetColumnFilter(key, '')} className="ml-1 hover:text-[#0D2747]">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Inventory Table */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="overflow-x-auto rounded-xl">
            <table className="inventory-table np-table border-collapse" style={{ tableLayout: 'fixed', width: '100%', minWidth: `${columnWidths.reduce((sum, width) => sum + width, 0)}px`, borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead className="inventory-table-thead">
                <tr>
                  <th className="inventory-table-header border-r border-white sticky-left-col" style={getHeaderStyle(0)}>
                    Edit
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(1)}>
                    SNO
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(1, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(2)}>
                    SPARE
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(2, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(3)}>
                    <div className="flex items-center justify-center gap-1">
                      EQUIPMENT NAME
                      <ChevronDown className="w-3 h-3" />
                    </div>
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(3, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(4)}>
                    BRIEF DESCRIPTION
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(4, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(5)}>
                    SERIAL NO.
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(5, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(6)}>
                    OEM
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(6, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(7)}>
                    DATASHEET
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(7, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(8)}>
                    APPARENT POWER
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(8, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(9)}>
                    ACTIVE POWER
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(9, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(10)}>
                    REACTIVE POWER
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(10, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(11)}>
                    VOLTAGE
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(11, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(12)}>
                    CURRENT
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(12, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(13)}>
                    FREQUENCY
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(13, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(14)}>
                    LEAD TIME
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(14, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(15)}>
                    ESTIMATED COST
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(15, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(16)}>
                    TOTAL INSTALLED QUANTITY
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(16, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(17)}>
                    IN-STOCK
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(17, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(18)}>
                    CONSUMPTION (1 MONTH)
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(18, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(19)}>
                    CONSUMPTION (3 MONTHS)
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(19, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header border-r border-white relative" style={getHeaderStyle(20)}>
                    CONSUMPTION (6 MONTHS)
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(20, e)}
                      title="Drag to resize column"
                    />
                  </th>
                  <th className="inventory-table-header relative" style={getHeaderStyle(21)}>
                    CONSUMPTION (12 MONTHS)
                    <div
                      className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-[#3EA3DA] bg-transparent z-10"
                      onMouseDown={(e) => handleResizeStart(21, e)}
                      title="Drag to resize column"
                    />
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInventory.map((item, index) => {
                  const status = getDerivedStatus(item);
                  const quantity = getNumericValue(item.quantity);
                  const minStock = getNumericValue(item.min_stock);
                  const maxStock = getNumericValue(item.max_stock);
                  const currentValue = getNumericValue(item.current_value);
                  const unitPrice = getNumericValue(item.unit_price);
                  const stockPercent = maxStock > 0 ? ((quantity / maxStock) * 100) : 0;
                  const isLowStock = status === 'low_stock' || status === 'out_of_stock';

                  return (
                    <React.Fragment key={item.id}>
                      <motion.tr
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: Math.min(index, 8) * 0.02 }}
                        className={`${expandedRow === item.id ? 'bg-[#F2F8FC]/50 hover:bg-[#F2F8FC]/60' : 'hover:bg-[#F2F8FC]/30'} transition-colors group border-b border-gray-100 cursor-pointer`}
                        onClick={() => setExpandedRow(prev => prev === item.id ? null : item.id)}
                      >
                        <td className="px-2 py-1.5 border-r border-gray-100 sticky-left-col" style={{ width: `${columnWidths[0]}px`, minWidth: `${columnWidths[0]}px`, maxWidth: `${columnWidths[0]}px`, overflow: 'hidden', position: 'sticky', left: 0, zIndex: 30, backgroundColor: expandedRow === item.id ? 'var(--np-tint)' : 'var(--np-surface)', borderRight: '1px solid var(--np-line)', boxShadow: '2px 0 5px -2px rgba(0,0,0,0.1)' }}>
                          <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleEditItem(item)}
                              className="p-1 text-[#0066A3] hover:bg-[#E3F0F9] rounded-lg transition-colors"
                              title="Edit item" aria-label="Edit item"
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleDeleteItem(item.id)}
                              className="p-1 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                              title="Delete item" aria-label="Delete item"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </motion.button>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[1]}px`, minWidth: `${columnWidths[1]}px`, maxWidth: `${columnWidths[1]}px`, overflow: 'hidden' }}>
                          <span className="text-xs text-gray-900 font-sans truncate block inventory-sno-text-small" title={String(item.sno ?? index + 1)}>{item.sno ?? index + 1}</span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[2]}px`, minWidth: `${columnWidths[2]}px`, maxWidth: `${columnWidths[2]}px`, overflow: 'hidden' }}>
                          <span className="text-xs text-gray-900 font-sans truncate block inventory-table-text" style={{ fontWeight: 400, fontSize: '11px' }} title={item.category ?? '-'}>{item.category ?? '-'}</span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[3]}px`, minWidth: `${columnWidths[3]}px`, maxWidth: `${columnWidths[3]}px`, overflow: 'hidden' }}>
                          <div className="flex items-center justify-between gap-2 w-full">
                            <span className="text-xs text-gray-900 font-sans truncate block inventory-table-text flex-1" style={{ fontWeight: 400, fontSize: '11px' }} title={item.equipment_name ?? '-'}>{item.equipment_name ?? '-'}</span>
                            <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform duration-200 flex-shrink-0 ${expandedRow === item.id ? 'rotate-180' : ''}`} />
                          </div>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[4]}px`, minWidth: `${columnWidths[4]}px`, maxWidth: `${columnWidths[4]}px`, overflow: 'hidden' }}>
                          <span className="text-xs text-gray-900 font-sans truncate block inventory-table-text" style={{ fontWeight: 400, fontSize: '11px' }} title={item.description ?? '-'}>{item.description ?? '-'}</span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[5]}px`, minWidth: `${columnWidths[5]}px`, maxWidth: `${columnWidths[5]}px`, overflow: 'hidden' }}>
                          <span className="text-xs text-gray-900 font-sans truncate block inventory-table-text" style={{ fontWeight: 400, fontSize: '11px' }} title={item.product_serial_number ?? '-'}>{item.product_serial_number ?? '-'}</span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[6]}px`, minWidth: `${columnWidths[6]}px`, maxWidth: `${columnWidths[6]}px`, overflow: 'hidden' }}>
                          <span className="text-xs text-gray-900 font-sans truncate block inventory-table-text" style={{ fontWeight: 400, fontSize: '11px' }} title={item.make ?? '-'}>{item.make ?? '-'}</span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[7]}px`, minWidth: `${columnWidths[7]}px`, maxWidth: `${columnWidths[7]}px`, overflow: 'hidden' }}>
                          <span className="text-xs text-gray-900 font-sans truncate block inventory-table-text" style={{ fontWeight: 400, fontSize: '11px' }} title={item.datasheet ?? '-'}>{item.datasheet ?? '-'}</span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[8]}px`, minWidth: `${columnWidths[8]}px`, maxWidth: `${columnWidths[8]}px`, overflow: 'hidden' }}>
                          <span className="text-xs text-gray-900 font-sans truncate block inventory-table-text" style={{ fontWeight: 400, fontSize: '11px' }} title={item.apparent_power_rating ?? '-'}>{item.apparent_power_rating ?? '-'}</span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[9]}px`, minWidth: `${columnWidths[9]}px`, maxWidth: `${columnWidths[9]}px`, overflow: 'hidden' }}>
                          <span className="text-xs text-gray-900 font-sans truncate block inventory-table-text" style={{ fontWeight: 400, fontSize: '11px' }} title={item.power_rating ?? '-'}>{item.power_rating ?? '-'}</span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[10]}px`, minWidth: `${columnWidths[10]}px`, maxWidth: `${columnWidths[10]}px`, overflow: 'hidden' }}>
                          <span className="text-xs text-gray-900 font-sans truncate block inventory-table-text" style={{ fontWeight: 400, fontSize: '11px' }} title={item.reactive_power_rating ?? '-'}>{item.reactive_power_rating ?? '-'}</span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[11]}px`, minWidth: `${columnWidths[11]}px`, maxWidth: `${columnWidths[11]}px`, overflow: 'hidden' }}>
                          <span className="text-xs text-gray-900 font-sans truncate block inventory-table-text" style={{ fontWeight: 400, fontSize: '11px' }} title={item.voltage_rating ?? '-'}>{item.voltage_rating ?? '-'}</span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[12]}px`, minWidth: `${columnWidths[12]}px`, maxWidth: `${columnWidths[12]}px`, overflow: 'hidden' }}>
                          <span className="text-xs text-gray-900 font-sans truncate block inventory-table-text" style={{ fontWeight: 400, fontSize: '11px' }} title={item.current_rating ?? '-'}>{item.current_rating ?? '-'}</span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[13]}px`, minWidth: `${columnWidths[13]}px`, maxWidth: `${columnWidths[13]}px`, overflow: 'hidden' }}>
                          <span className="text-xs text-gray-900 font-sans truncate block inventory-table-text" style={{ fontWeight: 400, fontSize: '11px' }} title={item.frequency_rating ?? '-'}>{item.frequency_rating ?? '-'}</span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[14]}px`, minWidth: `${columnWidths[14]}px`, maxWidth: `${columnWidths[14]}px`, overflow: 'hidden' }}>
                          <span className="inventory-table-text truncate block" style={{ fontWeight: 400, fontSize: '11px' }} title={String(item.lead_time ?? '-')}>{item.lead_time ?? '-'}</span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[15]}px`, minWidth: `${columnWidths[15]}px`, maxWidth: `${columnWidths[15]}px`, overflow: 'hidden' }}>
                          <span className="inventory-bold-number truncate block" title={String(getEstimatedCost(item))}>{getEstimatedCost(item).toLocaleString('en-IN')}</span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[16]}px`, minWidth: `${columnWidths[16]}px`, maxWidth: `${columnWidths[16]}px`, overflow: 'hidden' }}>
                          <span className="inventory-bold-number truncate block" title={String(item.installed_quantity ?? '-')}>{item.installed_quantity ?? '-'}</span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[17]}px`, minWidth: `${columnWidths[17]}px`, maxWidth: `${columnWidths[17]}px`, overflow: 'hidden' }}>
                          <span className={`bold-inventory-text inline-flex items-center px-1.5 py-0.5 rounded ${item.spare_availability ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-50 text-gray-600 border border-gray-200'}`}>
                            {item.spare_availability ?? 0}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[18]}px`, minWidth: `${columnWidths[18]}px`, maxWidth: `${columnWidths[18]}px`, overflow: 'hidden' }}>
                          <span className="inventory-numeric-bold truncate block" title={String(getConsumptionOneMonth(item))}>{getConsumptionOneMonth(item)}</span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[19]}px`, minWidth: `${columnWidths[19]}px`, maxWidth: `${columnWidths[19]}px`, overflow: 'hidden' }}>
                          <span className="inventory-bold-number truncate block" title={String(item.consumption_pattern_last_three_months ?? '-')}>{item.consumption_pattern_last_three_months ?? '-'}</span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[20]}px`, minWidth: `${columnWidths[20]}px`, maxWidth: `${columnWidths[20]}px`, overflow: 'hidden' }}>
                          <span className="inventory-bold-number truncate block" title={String(item.consumption_pattern_last_six_months ?? '-')}>{item.consumption_pattern_last_six_months ?? '-'}</span>
                        </td>
                        <td className="px-2 py-1.5 border-r border-gray-100" style={{ width: `${columnWidths[21]}px`, minWidth: `${columnWidths[21]}px`, maxWidth: `${columnWidths[21]}px`, overflow: 'hidden' }}>
                          <span className="bold-inventory-text truncate block" title={String(item.consumption_pattern_last_twelve_months ?? '-')}>{item.consumption_pattern_last_twelve_months ?? '-'}</span>
                        </td>
                      </motion.tr>
                      {expandedRow === item.id && (
                        <tr className="bg-slate-50/30">
                          <td colSpan={22} className="inventory-history-cell border-gray-200">
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="inventory-history-wrapper"
                            >
                              <InventoryHistory item={item} />
                            </motion.div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredInventory.length === 0 && !loading && (
            <div className="text-center py-16">
              <div className="flex flex-col items-center justify-center">
                <div className="p-4 bg-gray-100 rounded-full mb-4">
                  <Package className="w-12 h-12 text-gray-400" />
                </div>
                <p className="text-lg font-normal text-gray-900 mb-2">No inventory items found</p>
                <p className="text-sm text-gray-500 mb-4">
                  {searchTerm || Object.keys(columnFilters).length > 0
                    ? 'Try adjusting your search or filters to see more results'
                    : 'Get started by adding your first inventory item'}
                </p>
                {(!searchTerm && Object.keys(columnFilters).length === 0) && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setEditingItem(null);
                      setNewItem({
                        item_code: '',
                        item_name: '',
                        category: '',
                        quantity: '',
                        unit: 'pcs',
                        min_stock: '',
                        max_stock: '',
                        unit_price: '',
                        location: '',
                        supplier: '',
                        batch_number: '',
                        expiry_date: '',
                        notes: '',
                        sno: '',
                        equipment_name: '',
                        description: '',
                        product_serial_number: '',
                        electrical_rating: '',
                        current_rating: '',
                        voltage_rating: '',
                        power_rating: '',
                        reactive_power_rating: '',
                        apparent_power_rating: '',
                        frequency_rating: '',
                        capacity_amp_kw_kva: '',
                        make: '',
                        datasheet: '',
                        installed_quantity: '',
                        spare_availability: 0,
                        consumption_pattern_last_three_months: '',
                        consumption_pattern_last_six_months: '',
                        consumption_pattern_last_twelve_months: '',
                        lead_time: ''
                      });
                      setShowAddModal(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-[#0066A3] text-white rounded-lg hover:bg-[#1C4D88] transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add First Item
                  </motion.button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Add/Edit Item Modal */}
        {showAddModal && createPortal(
          <AnimatePresence>
            <div
              className="np-theme fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex min-h-screen items-center justify-center z-[10000] p-4 sm:p-6 pt-20"
              onClick={() => {
                setShowAddModal(false);
                setEditingItem(null);
                setNewItem({
                  item_code: '',
                  item_name: '',
                  category: '',
                  quantity: '',
                  unit: 'pcs',
                  min_stock: '',
                  max_stock: '',
                  unit_price: '',
                  location: '',
                  supplier: '',
                  batch_number: '',
                  expiry_date: '',
                  notes: '',
                  sno: '',
                  equipment_name: '',
                  description: '',
                  product_serial_number: '',
                  electrical_rating: '',
                  current_rating: '',
                  voltage_rating: '',
                  power_rating: '',
                  reactive_power_rating: '',
                  apparent_power_rating: '',
                  frequency_rating: '',
                  capacity_amp_kw_kva: '',
                  make: '',
                  datasheet: '',
                  installed_quantity: '',
                  spare_availability: 0,
                  consumption_pattern_last_three_months: '',
                  consumption_pattern_last_six_months: '',
                  consumption_pattern_last_twelve_months: '',
                  lead_time: ''
                });
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl border border-slate-100 shadow-[0_20px_80px_rgba(15,23,42,0.12)] w-full max-w-4xl max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col"
              >
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-[#0066A3] to-[#0066A3] px-6 py-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-normal text-white" style={{ fontWeight: 400 }}>
                      {editingItem ? 'Edit Inventory Item' : 'Add New Inventory Item'}
                    </h3>
                    <p className="text-[#E3F0F9] text-sm mt-0.5 font-normal" style={{ fontWeight: 400 }}>
                      {editingItem ? 'Update item details below' : 'Fill in the details to add a new item'}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingItem(null);
                      setNewItem({
                        item_code: '',
                        item_name: '',
                        category: '',
                        quantity: '',
                        unit: 'pcs',
                        min_stock: '',
                        max_stock: '',
                        unit_price: '',
                        location: '',
                        supplier: '',
                        batch_number: '',
                        expiry_date: '',
                        notes: '',
                        sno: '',
                        equipment_name: '',
                        description: '',
                        product_serial_number: '',
                        electrical_rating: '',
                        current_rating: '',
                        voltage_rating: '',
                        power_rating: '',
                        reactive_power_rating: '',
                        apparent_power_rating: '',
                        frequency_rating: '',
                        capacity_amp_kw_kva: '',
                        make: '',
                        datasheet: '',
                        installed_quantity: '',
                        spare_availability: 0,
                        consumption_pattern_last_three_months: '',
                        consumption_pattern_last_six_months: '',
                        consumption_pattern_last_twelve_months: '',
                        lead_time: ''
                      });
                    }}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
                  <div className="space-y-8">
                    {/* section 1: Core Information */}
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                      <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
                        <div className="p-1.5 bg-[#E3F0F9] rounded-lg">
                          <Package className="w-4 h-4 text-[#0066A3]" />
                        </div>
                        <h4 className="text-base font-normal text-black uppercase tracking-tight" style={{ fontWeight: 400 }}>Core Information</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>
                            Equipment Name <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={newItem.equipment_name}
                            onChange={(e) => setNewItem({ ...newItem, equipment_name: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm bg-white"
                            required
                          >
                            <option value="">Select Equipment</option>
                            {equipmentOptions.map((equipment) => (
                              <option key={equipment} value={equipment}>{equipment}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Spare</label>
                          <select
                            value={newItem.category}
                            onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm bg-white"
                          >
                            <option value="">Select Spare</option>
                            {spareOptionsForSelectedEquipment.map((spare) => (
                              <option key={spare} value={spare}>{spare}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>SNO (Internal)</label>
                          <input
                            type="number"
                            value={newItem.sno}
                            onChange={(e) => setNewItem({ ...newItem, sno: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm font-mono"
                            placeholder="e.g. 1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Product Serial No.</label>
                          <input
                            type="text"
                            value={newItem.product_serial_number}
                            onChange={(e) => setNewItem({ ...newItem, product_serial_number: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm font-mono"
                            placeholder="e.g. SN-987654321"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Make (Manufacturer)</label>
                          <input
                            type="text"
                            value={newItem.make}
                            onChange={(e) => setNewItem({ ...newItem, make: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm"
                            placeholder="e.g. Trina Solar"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Section 2: Technical Specifications */}
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                      <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
                        <div className="p-1.5 bg-amber-100 rounded-lg">
                          <Activity className="w-4 h-4 text-amber-600" />
                        </div>
                        <h4 className="text-base font-normal text-black uppercase tracking-tight" style={{ fontWeight: 400 }}>Technical Specifications</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Electrical Rating</label>
                          <input
                            type="text"
                            value={newItem.electrical_rating}
                            onChange={(e) => setNewItem({ ...newItem, electrical_rating: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm"
                            placeholder="e.g. 1500V DC"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Current Rating</label>
                          <input
                            type="text"
                            value={newItem.current_rating}
                            onChange={(e) => setNewItem({ ...newItem, current_rating: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm"
                            placeholder="e.g. 15A"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Voltage Rating</label>
                          <input
                            type="text"
                            value={newItem.voltage_rating}
                            onChange={(e) => setNewItem({ ...newItem, voltage_rating: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm"
                            placeholder="e.g. 1000V"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Power Rating</label>
                          <input
                            type="text"
                            value={newItem.power_rating}
                            onChange={(e) => setNewItem({ ...newItem, power_rating: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm"
                            placeholder="e.g. 545W"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Reactive Power</label>
                          <input
                            type="text"
                            value={newItem.reactive_power_rating}
                            onChange={(e) => setNewItem({ ...newItem, reactive_power_rating: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm"
                            placeholder="e.g. 0 VAR"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Apparent Power</label>
                          <input
                            type="text"
                            value={newItem.apparent_power_rating}
                            onChange={(e) => setNewItem({ ...newItem, apparent_power_rating: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm"
                            placeholder="e.g. 545VA"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Frequency</label>
                          <input
                            type="text"
                            value={newItem.frequency_rating}
                            onChange={(e) => setNewItem({ ...newItem, frequency_rating: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm"
                            placeholder="e.g. 50/60Hz"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Capacity (Amp/KW/KVA)</label>
                          <input
                            type="text"
                            value={newItem.capacity_amp_kw_kva}
                            onChange={(e) => setNewItem({ ...newItem, capacity_amp_kw_kva: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm"
                            placeholder="e.g. 100KVA"
                          />
                        </div>
                        <div className="md:col-span-4">
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Datasheet URL</label>
                          <div className="relative">
                            <div className="absolute left-3 top-1/2 -translate-y-1/2">
                              <FileText className="w-4 h-4 text-slate-400" />
                            </div>
                            <input
                              type="url"
                              value={newItem.datasheet}
                              onChange={(e) => setNewItem({ ...newItem, datasheet: e.target.value })}
                              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm"
                              placeholder="https://example.com/spec.pdf"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Section 3: Quantity & Performance Tracking */}
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                      <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
                        <div className="p-1.5 bg-emerald-100 rounded-lg">
                          <TrendingUp className="w-4 h-4 text-emerald-600" />
                        </div>
                        <h4 className="text-base font-normal text-black uppercase tracking-tight" style={{ fontWeight: 400 }}>Quantity & Performance</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Installed Qty</label>
                          <input
                            type="number"
                            value={newItem.installed_quantity}
                            onChange={(e) => setNewItem({ ...newItem, installed_quantity: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm"
                            placeholder="0"
                            min="0"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Spare Availability</label>
                          <input
                            type="number"
                            value={newItem.spare_availability}
                            onChange={(e) => setNewItem({ ...newItem, spare_availability: parseFloat(e.target.value) || 0 })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm"
                            placeholder="0"
                            min="0"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Lead Time (Days)</label>
                          <input
                            type="number"
                            value={newItem.lead_time}
                            onChange={(e) => setNewItem({ ...newItem, lead_time: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm"
                            placeholder="0"
                          />
                        </div>
                        <div className="md:col-start-1">
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Consumption (3m)</label>
                          <input
                            type="number"
                            value={newItem.consumption_pattern_last_three_months}
                            onChange={(e) => setNewItem({ ...newItem, consumption_pattern_last_three_months: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm"
                            placeholder="Last 3 mo"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Consumption (6m)</label>
                          <input
                            type="number"
                            value={newItem.consumption_pattern_last_six_months}
                            onChange={(e) => setNewItem({ ...newItem, consumption_pattern_last_six_months: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm"
                            placeholder="Last 6 mo"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Consumption (12m)</label>
                          <input
                            type="number"
                            value={newItem.consumption_pattern_last_twelve_months}
                            onChange={(e) => setNewItem({ ...newItem, consumption_pattern_last_twelve_months: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm"
                            placeholder="Last 12 mo"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Section 4: Maintenance Notes */}
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                      <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
                        <div className="p-1.5 bg-slate-100 rounded-lg">
                          <FileText className="w-4 h-4 text-slate-600" />
                        </div>
                        <h4 className="text-base font-normal text-black uppercase tracking-tight" style={{ fontWeight: 400 }}>Additional Details</h4>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-normal text-black uppercase tracking-wider mb-1.5" style={{ fontWeight: 400 }}>Description / Specifications</label>
                          <textarea
                            value={newItem.description}
                            onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                            rows={3}
                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all text-sm resize-none bg-slate-50/30"
                            placeholder="Enter detailed technical description or specifications..."
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="bg-gray-50 px-6 py-4 flex gap-3 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingItem(null);
                      setNewItem({
                        item_code: '',
                        item_name: '',
                        category: '',
                        quantity: '',
                        unit: 'pcs',
                        min_stock: '',
                        max_stock: '',
                        unit_price: '',
                        location: '',
                        supplier: '',
                        batch_number: '',
                        expiry_date: '',
                        notes: '',
                        sno: '',
                        equipment_name: '',
                        description: '',
                        product_serial_number: '',
                        electrical_rating: '',
                        current_rating: '',
                        voltage_rating: '',
                        power_rating: '',
                        reactive_power_rating: '',
                        apparent_power_rating: '',
                        frequency_rating: '',
                        capacity_amp_kw_kva: '',
                        make: '',
                        datasheet: '',
                        installed_quantity: '',
                        spare_availability: 0,
                        consumption_pattern_last_three_months: '',
                        consumption_pattern_last_six_months: '',
                        consumption_pattern_last_twelve_months: '',
                        lead_time: ''
                      });
                    }}
                    className="flex-1 px-5 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={editingItem ? handleUpdateItem : handleAddItem}
                    className="flex-1 px-5 py-2.5 bg-gradient-to-r from-[#0066A3] to-[#0066A3] text-white rounded-lg hover:from-[#1C4D88] hover:to-[#1C4D88] transition-all font-semibold shadow-md"
                  >
                    {editingItem ? 'Update Item' : 'Add Item'}
                  </motion.button>
                </div>
              </motion.div>
            </div>
          </AnimatePresence>
          , document.body)}

        {/* View Item Modal */}
        {showViewModal && viewingItem && createPortal(
          <AnimatePresence>
            <div
              className="np-theme fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex min-h-screen items-center justify-center z-[10000] p-4 sm:p-6 pt-20"
              onClick={() => {
                setShowViewModal(false);
                setViewingItem(null);
              }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-2xl border border-slate-100 shadow-[0_20px_80px_rgba(15,23,42,0.12)] w-full max-w-3xl max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col"
              >
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-[#0066A3] to-[#0066A3] px-6 py-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-white">Item Details</h3>
                    <p className="text-[#E3F0F9] text-sm mt-0.5">{viewingItem.item_code}</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setViewingItem(null);
                    }}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="flex-1 overflow-y-auto bg-slate-50">
                  <div className="p-6 space-y-6">
                    <section className="rounded-2xl border border-slate-100 bg-white/90 p-6 shadow-sm">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-500">{viewingItem.category}</p>
                          <h4 className="text-2xl font-normal text-slate-900">{viewingItem.item_name}</h4>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(getDerivedStatus(viewingItem))}`}>
                            {getStatusIcon(getDerivedStatus(viewingItem))}
                            <span className="capitalize">{getDerivedStatus(viewingItem).replace('_', ' ')}</span>
                          </span>
                          <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 bg-white">
                            Code: {viewingItem.item_code}
                          </span>
                        </div>
                      </div>
                      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <div className="rounded-xl bg-slate-50/80 px-4 py-3">
                          <p className="text-xs font-normal text-black uppercase" style={{ fontWeight: 400 }}>Quantity</p>
                          <p className="text-xl font-normal text-black mt-1" style={{ fontWeight: 400 }}>{(viewingItem.quantity ?? 0).toLocaleString()} {viewingItem.unit || ''}</p>
                        </div>
                        <div className="rounded-xl bg-slate-50/80 px-4 py-3">
                          <p className="text-xs font-normal text-black uppercase" style={{ fontWeight: 400 }}>Total Value</p>
                          <p className="text-xl font-normal text-black mt-1" style={{ fontWeight: 400 }}>₹{(viewingItem.current_value ?? 0).toLocaleString()}</p>
                        </div>
                        <div className="rounded-xl bg-slate-50/80 px-4 py-3">
                          <p className="text-xs font-normal text-black uppercase" style={{ fontWeight: 400 }}>Unit Price</p>
                          <p className="text-lg font-normal text-black mt-1" style={{ fontWeight: 400 }}>₹{(viewingItem.unit_price ?? 0).toLocaleString()}</p>
                        </div>
                        <div className="rounded-xl bg-slate-50/80 px-4 py-3">
                          <p className="text-xs font-normal text-black uppercase" style={{ fontWeight: 400 }}>Stock Range</p>
                          <p className="text-sm font-normal text-black mt-1" style={{ fontWeight: 400 }}>Min: {viewingItem.min_stock} | Max: {viewingItem.max_stock}</p>
                        </div>
                      </div>
                    </section>

                    <section className="grid gap-4 sm:grid-cols-2">
                      <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                        <p className="text-[12px] font-normal uppercase tracking-wide text-black" style={{ fontWeight: 400 }}>Location</p>
                        <p className="mt-3 text-lg font-normal text-black" style={{ fontWeight: 400 }}>
                          {viewingItem.location || 'Not specified'}
                        </p>
                      </div>
                      <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                        <p className="text-[12px] font-normal uppercase tracking-wide text-black" style={{ fontWeight: 400 }}>Supplier / Batch</p>
                        <div className="mt-3 space-y-1.5">
                          <p className="text-lg font-normal text-black" style={{ fontWeight: 400 }}>{viewingItem.supplier || '—'}</p>
                          <p className="text-sm font-normal font-mono text-black" style={{ fontWeight: 400 }}>{viewingItem.batch_number || '—'}</p>
                        </div>
                      </div>
                    </section>

                    <section className="grid gap-4 sm:grid-cols-2">
                      {viewingItem.expiry_date && (
                        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                          <p className="text-xs font-normal text-black uppercase mb-2" style={{ fontWeight: 400 }}>Expiry Date</p>
                          <div className="flex items-center gap-2 text-sm text-black" style={{ fontWeight: 400 }}>
                            <Calendar className="w-4 h-4 text-slate-400" />
                            {formatDate(viewingItem.expiry_date)}
                          </div>
                        </div>
                      )}

                      <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                        <p className="text-[12px] font-normal uppercase tracking-wide text-black" style={{ fontWeight: 400 }}>Last Updated</p>
                        <p className="mt-3 text-lg font-normal text-black" style={{ fontWeight: 400 }}>
                          {viewingItem.last_updated ? formatDate(viewingItem.last_updated) : 'Not available'}
                        </p>
                      </div>
                    </section>

                    {viewingItem.notes && (
                      <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                        <p className="text-[12px] font-normal uppercase tracking-wide text-black" style={{ fontWeight: 400 }}>Notes</p>
                        <p className="mt-3 text-base font-normal text-black whitespace-pre-wrap leading-relaxed" style={{ fontWeight: 400 }}>
                          {viewingItem.notes}
                        </p>
                      </section>
                    )}

                    {(viewingItem.sno || viewingItem.station || viewingItem.equipment_name || viewingItem.description || viewingItem.product_serial_number || viewingItem.make || viewingItem.datasheet) && (
                      <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-normal text-black" style={{ fontWeight: 400 }}>Equipment Details</h4>
                          {viewingItem.datasheet && (
                            <a
                              href={viewingItem.datasheet}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-semibold text-[#0066A3] hover:text-[#1C4D88]"
                            >
                              View Datasheet
                            </a>
                          )}
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                          {viewingItem.sno && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>SNO</p>
                              <p className="text-sm font-mono text-black" style={{ fontWeight: 400 }}>{viewingItem.sno}</p>
                            </div>
                          )}
                          {viewingItem.station && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Station</p>
                              <p className="text-sm text-black" style={{ fontWeight: 400 }}>{viewingItem.station}</p>
                            </div>
                          )}
                          {viewingItem.equipment_name && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Equipment</p>
                              <p className="text-sm text-black" style={{ fontWeight: 400 }}>{viewingItem.equipment_name}</p>
                            </div>
                          )}
                          {viewingItem.product_serial_number && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Serial No.</p>
                              <p className="text-sm font-mono text-black" style={{ fontWeight: 400 }}>{viewingItem.product_serial_number}</p>
                            </div>
                          )}
                          {viewingItem.make && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Make</p>
                              <p className="text-sm text-black" style={{ fontWeight: 400 }}>{viewingItem.make}</p>
                            </div>
                          )}
                        </div>
                        {viewingItem.description && (
                          <div className="mt-4 rounded-xl bg-slate-50/80 px-4 py-3">
                            <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Description</p>
                            <p className="text-sm text-black whitespace-pre-wrap" style={{ fontWeight: 400 }}>{viewingItem.description}</p>
                          </div>
                        )}
                      </section>
                    )}

                    {(viewingItem.electrical_rating || viewingItem.current_rating || viewingItem.voltage_rating || viewingItem.power_rating || viewingItem.reactive_power_rating || viewingItem.apparent_power_rating || viewingItem.frequency_rating || viewingItem.capacity_amp_kw_kva) && (
                      <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                        <h4 className="text-sm font-normal text-black mb-4" style={{ fontWeight: 400 }}>Electrical Ratings</h4>
                        <div className="grid gap-4 sm:grid-cols-2">
                          {viewingItem.electrical_rating && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Electrical Rating</p>
                              <p className="text-sm text-black" style={{ fontWeight: 400 }}>{viewingItem.electrical_rating}</p>
                            </div>
                          )}
                          {viewingItem.current_rating && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Current Rating</p>
                              <p className="text-sm text-black" style={{ fontWeight: 400 }}>{viewingItem.current_rating}</p>
                            </div>
                          )}
                          {viewingItem.voltage_rating && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Voltage Rating</p>
                              <p className="text-sm text-black" style={{ fontWeight: 400 }}>{viewingItem.voltage_rating}</p>
                            </div>
                          )}
                          {viewingItem.power_rating && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Power Rating</p>
                              <p className="text-sm text-black" style={{ fontWeight: 400 }}>{viewingItem.power_rating}</p>
                            </div>
                          )}
                          {viewingItem.reactive_power_rating && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Reactive Power Rating</p>
                              <p className="text-sm text-black" style={{ fontWeight: 400 }}>{viewingItem.reactive_power_rating}</p>
                            </div>
                          )}
                          {viewingItem.apparent_power_rating && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Apparent Power Rating</p>
                              <p className="text-sm text-black" style={{ fontWeight: 400 }}>{viewingItem.apparent_power_rating}</p>
                            </div>
                          )}
                          {viewingItem.frequency_rating && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Frequency Rating</p>
                              <p className="text-sm text-black" style={{ fontWeight: 400 }}>{viewingItem.frequency_rating}</p>
                            </div>
                          )}
                          {viewingItem.capacity_amp_kw_kva && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Capacity (Amp/KW/KVA)</p>
                              <p className="text-sm text-black" style={{ fontWeight: 400 }}>{viewingItem.capacity_amp_kw_kva}</p>
                            </div>
                          )}
                        </div>
                      </section>
                    )}

                    {((viewingItem.installed_quantity !== null && viewingItem.installed_quantity !== undefined) || (viewingItem.lead_time !== null && viewingItem.lead_time !== undefined) || (viewingItem.consumption_pattern_last_three_months !== null && viewingItem.consumption_pattern_last_three_months !== undefined) || (viewingItem.consumption_pattern_last_six_months !== null && viewingItem.consumption_pattern_last_six_months !== undefined) || (viewingItem.consumption_pattern_last_twelve_months !== null && viewingItem.consumption_pattern_last_twelve_months !== undefined) || viewingItem.spare_availability !== undefined) && (
                      <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                        <h4 className="text-sm font-normal text-black mb-4" style={{ fontWeight: 400 }}>Quantity & Consumption</h4>
                        <div className="grid gap-4 sm:grid-cols-2">
                          {viewingItem.installed_quantity !== null && viewingItem.installed_quantity !== undefined && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Installed Quantity</p>
                              <p className="text-sm text-black" style={{ fontWeight: 400 }}>{(viewingItem.installed_quantity ?? 0).toLocaleString()}</p>
                            </div>
                          )}
                          {viewingItem.lead_time !== null && viewingItem.lead_time !== undefined && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Lead Time</p>
                              <p className="text-sm text-black" style={{ fontWeight: 400 }}>{viewingItem.lead_time} days</p>
                            </div>
                          )}
                          {viewingItem.consumption_pattern_last_three_months !== null && viewingItem.consumption_pattern_last_three_months !== undefined && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Consumption - Last 3 Months</p>
                              <p className="text-sm text-black" style={{ fontWeight: 400 }}>{(viewingItem.consumption_pattern_last_three_months ?? 0).toLocaleString()}</p>
                            </div>
                          )}
                          {viewingItem.consumption_pattern_last_six_months !== null && viewingItem.consumption_pattern_last_six_months !== undefined && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Consumption - Last 6 Months</p>
                              <p className="text-sm text-black" style={{ fontWeight: 400 }}>{(viewingItem.consumption_pattern_last_six_months ?? 0).toLocaleString()}</p>
                            </div>
                          )}
                          {viewingItem.consumption_pattern_last_twelve_months !== null && viewingItem.consumption_pattern_last_twelve_months !== undefined && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Consumption - Last 12 Months</p>
                              <p className="text-sm text-black" style={{ fontWeight: 400 }}>{(viewingItem.consumption_pattern_last_twelve_months ?? 0).toLocaleString()}</p>
                            </div>
                          )}
                          {viewingItem.spare_availability !== undefined && (
                            <div>
                              <p className="text-xs font-normal text-black uppercase mb-1" style={{ fontWeight: 400 }}>Spare Availability</p>
                              <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${viewingItem.spare_availability ? 'bg-green-100' : 'bg-slate-100'}`} style={{ color: '#000000', fontWeight: 400 }}>
                                {viewingItem.spare_availability ? (viewingItem.spare_availability > 0 ? viewingItem.spare_availability : 'Not Available') : 'Not Available'}
                              </span>
                            </div>
                          )}
                        </div>
                      </section>
                    )}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="bg-gray-50 px-6 py-4 flex gap-3 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setViewingItem(null);
                    }}
                    className="flex-1 px-5 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                  >
                    Close
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowViewModal(false);
                      handleEditItem(viewingItem);
                    }}
                    className="flex-1 px-5 py-2.5 bg-gradient-to-r from-[#0066A3] to-[#0066A3] text-white rounded-lg hover:from-[#1C4D88] hover:to-[#1C4D88] transition-all font-semibold shadow-md"
                  >
                    Edit Item
                  </motion.button>
                </div>
              </motion.div>
            </div>
          </AnimatePresence>
          , document.body)}
      </div>
    </div>
  );
};

export default InventoryPage;


